import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

function applyThemeVariables(variant: 'dark' | 'light') {
  if (!window.theme) return;

  const variables = window.theme[variant];
  if (!variables) return;

  const root = window.document.documentElement;

  // Preserve user's custom font settings
  const cachedFont = localStorage.getItem('org-font');
  const cachedFontSize = localStorage.getItem('org-font-size');

  // Apply new theme variables
  Object.entries(variables).forEach(([key, value]) => {
    // Don't override font if user has set a custom one
    if (key === '--font-sans' && cachedFont) {
      root.style.setProperty(key, `'${cachedFont}', sans-serif`);
    } else {
      root.style.setProperty(key, value);
    }
  });

  // Reapply font size if user has set one
  if (cachedFontSize) {
    root.style.fontSize = cachedFontSize;
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );
  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      applyThemeVariables(systemTheme);
      return;
    } else {
      applyThemeVariables(theme);
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    }
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

  const variant = context.theme === 'system' ? systemTheme : context.theme;

  return { ...context, variant };
};
